import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Tabs, Tooltip } from '@meitu/saas-ui';
import { ITabItemProps } from '@meitu/saas-ui/es/tabs/interface';
import { ocrImageFileToText, parseQRCodeToText } from '@meitu/saas-editor-core/es/layers/qr-code-layer';
import IconFont from '@meitu/xdesign-icon-font';
import { getLocaleStore, useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { isIntl } from '@/react-process-env';
import { getWxOfficialQrCode } from '@/services/qrcode';

enum TabType {
    text = 'text',
    official = 'official',
}

const TAB_DATA: () => ITabItemProps[] = () => {
    const { $t } = getLocaleStore();

    return [
        {
            name: $t((v) => v.panel.qrCodePanel.website),
            tabKey: TabType.text,
        },
        {
            name: $t((v) => v.panel.qrCodePanel.official),
            tabKey: TabType.official,
        },
    ];
};

export const InfoFeature: React.FC<{
    id: string;
    text: string;
    official: string;
}> = React.memo(({ id, text: originText, official: originOfficial }) => {
    const { $t } = useLocaleStore();
    const [tabType, setTabType] = useState<TabType>(TabType.text);
    const selectFile = useRef<any>(null);

    const [newText, setNewText] = useState('');
    const [newOfficial, setNewOfficial] = useState('');
    const [toolTip, setToolTip] = useState({ open: false, text: '' });

    useEffect(() => {
        setNewText(originText);
    }, [id, originText]);

    useEffect(() => {
        setNewOfficial(originOfficial);
        setTabType(originOfficial ? TabType.official : TabType.text);
    }, [id, originOfficial]);

    useEffect(() => {
        if (toolTip.open) {
            setTimeout(() => {
                setToolTip((pre) => {
                    return {
                        ...pre,
                        open: false,
                    };
                });
            }, 2000);
        }
    }, [toolTip.open]);

    const [isOcr, setIsOcr] = useState(false);

    const fileOcrHandle = (e: any) => {
        if (!e.target.files?.[0]) return;
        setIsOcr(true);
        ocrImageFileToText(e.target.files?.[0]).then((res: string) => {
            if (res) {
                setNewText(res);
            } else {
                setToolTip({
                    open: true,
                    text: $t((v) => v.panel.qrCodePanel.messageError.recognitionFailed),
                });
            }
            console.log('ocr text', res);
            setIsOcr(false);
        });
    };

    const updateQrCodeByUrl = () => {
        console.log('newText', newText);
        if (!newText) {
            return setToolTip({
                open: true,
                text: $t((v) => v.panel.qrCodePanel.messageError.websiteInputError),
            });
        }
        editor.qrCodeLayer.updateContent(
            id,
            {
                text: newText,
                official: '',
            },
            {
                trackProps: 'text',
                writeHistory: true,
            },
        );
    };

    const updateQrCodeByOfficial = () => {
        const tipText = $t((v) => v.panel.qrCodePanel.messageError.officialInputError);
        if (!newOfficial) return setToolTip({ open: true, text: tipText });
        getWxOfficialQrCode(newOfficial)
            .then((res) => {
                const base64 = res.data.data.base64;
                parseQRCodeToText(`data:image/jpeg;base64,${base64}`).then((text: string | undefined) => {
                    if (!text) {
                        setToolTip({ open: true, text: tipText });
                    } else {
                        setNewText(text);
                        editor.qrCodeLayer.updateContent(
                            id,
                            {
                                text,
                                official: newOfficial,
                            },
                            {
                                trackProps: 'official',
                                writeHistory: true,
                            },
                        );
                    }
                });
            })
            .catch(() => {
                setToolTip({ open: true, text: tipText });
            });
    };

    return (
        <div className="c-qrcode-panel__info">
            {!isIntl ? (
                <Tabs
                    data={TAB_DATA()}
                    activeIndex={tabType === TabType.text ? 1 : 2}
                    tabsHeight="34px"
                    handleTabClick={(key) => {
                        setTabType(key as TabType);
                        setToolTip({ open: false, text: '' });
                    }}
                />
            ) : (
                <div className="c-qrcode-panel__tipText">
                    {$t((v) => v.collapse.moreCollapse.componentTool.qrCode.intlTabTip)}
                </div>
            )}
            {tabType === TabType.text ? (
                <>
                    <div className="c-qrcode-panel__info-ocr">
                        <Tooltip
                            open={toolTip.open}
                            title={toolTip.text}
                            overlayClassName={classnames({
                                'c-qrcode-panel__info-tooltips': toolTip.text.length > 14,
                            })}
                        >
                            <Input
                                key="text"
                                value={newText}
                                className={classnames('c-qrcode-panel__info-input', {
                                    'c-qrcode-panel__info-input-warning': toolTip.open,
                                })}
                                placeholder={$t((v) => v.panel.qrCodePanel.websitePlaceholder)}
                                onChange={(e) => {
                                    let { value } = e.target;
                                    value = value.trimStart();
                                    if (value === newText) return;
                                    if (originText === value) {
                                        setNewOfficial(originOfficial);
                                    } else {
                                        setNewOfficial('');
                                    }
                                    setNewText(value);
                                }}
                                style={{ width: 182 }}
                            />
                        </Tooltip>
                        <Tooltip placement="bottom" title={$t((v) => v.panel.qrCodePanel.uploadIdentify)}>
                            <div
                                onClick={() => {
                                    if (isOcr) return;
                                    selectFile.current?.click();
                                }}
                                className={classnames('c-qrcode-panel__info-ocr-icon', {
                                    'c-qrcode-panel__info-ocr-loading': isOcr,
                                })}
                            >
                                {isOcr ? (
                                    <IconFont name="LoadingCircleBold" fontSize={16} />
                                ) : (
                                    <IconFont name="PictureAddBold" fontSize={16} />
                                )}
                            </div>
                        </Tooltip>
                        <input
                            accept="image/jpeg, image/png, image/gif"
                            type="file"
                            style={{ width: 0, height: 0, opacity: 0 }}
                            ref={selectFile}
                            onChange={fileOcrHandle}
                        />
                    </div>
                    <div className="c-qrcode-panel__info-btns">
                        <Button
                            key="text"
                            type="primary"
                            data-blue
                            block
                            onClick={() => {
                                updateQrCodeByUrl();
                            }}
                        >
                            {$t((v) => v.panel.qrCodePanel.updateQrCode)}
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    <div className="c-qrcode-panel__info-ocr">
                        <Tooltip open={toolTip.open} title={toolTip.text}>
                            <Input
                                key="official"
                                value={newOfficial}
                                className={classnames('c-qrcode-panel__info-input', {
                                    'c-qrcode-panel__info-input-warning': toolTip.open,
                                })}
                                placeholder={$t((v) => v.panel.qrCodePanel.officialPlaceholder)}
                                onChange={(e) => {
                                    let { value } = e.target;
                                    value = value.trimStart();
                                    if (value === newOfficial) return;
                                    setNewOfficial(value);
                                }}
                            />
                        </Tooltip>
                    </div>
                    <div className="c-qrcode-panel__info-btns">
                        <Button
                            key="official"
                            type="primary"
                            data-blue
                            block
                            onClick={() => {
                                updateQrCodeByOfficial();
                            }}
                        >
                            {$t((v) => v.panel.qrCodePanel.updateQrCode)}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
});
